import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ChevronLeft, ChevronRight, CalendarDays, LayoutGrid,
  Clock, MapPin, ArrowRight, X, Users
} from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, addWeeks, isBefore } from 'date-fns';
import { fr, nl, enUS } from 'date-fns/locale';
import axios from 'axios';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPACE_COLORS = ['#1A3C34', '#C77D63', '#4A90D9', '#7B68EE', '#E8D44D', '#9C27B0', '#2196F3', '#FF9800', '#4CAF50', '#607D8B'];
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6);

// Returns black or white text color depending on background luminance (W3C contrast)
function readableText(hex) {
  if (!hex || typeof hex !== 'string') return '#FFFFFF';
  const c = hex.replace('#', '');
  const full = c.length === 3 ? c.split('').map(x => x + x).join('') : c;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // Relative luminance formula
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#1A3C34' : '#FFFFFF';
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  useAuth(); // hook mounted for downstream context freshness even though no field is read here
  const dateLocale = i18n.language?.startsWith('nl') ? nl : i18n.language?.startsWith('en') ? enUS : fr;
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [spaceFilter, setSpaceFilter] = useState('all');
  const [calendarData, setCalendarData] = useState({});
  const [closedMap, setClosedMap] = useState({}); // {date: reason}
  const [loading, setLoading] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        const res = await axios.get(`${API}/spaces`);
        setSpaces(res.data.map((s, i) => ({
          id: s.space_id,
          name: s.name,
          color: SPACE_COLORS[i % SPACE_COLORS.length],
          shortName: s.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3)
        })));
      } catch { /* ignore */ }
    };
    fetchSpaces();
  }, []);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    let startDate, endDate;
    if (viewMode === 'month') {
      const ms = startOfMonth(currentDate);
      const me = endOfMonth(currentDate);
      startDate = format(startOfWeek(ms, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      endDate = format(endOfWeek(me, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    } else {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      startDate = format(ws, 'yyyy-MM-dd');
      endDate = format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    }
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (spaceFilter !== 'all') params.append('space_id', spaceFilter);
      const [availRes, closedRes] = await Promise.all([
        axios.get(`${API}/availability/range?${params}`),
        axios.get(`${API}/closed-days?start=${startDate}&end=${endDate}`),
      ]);
      setCalendarData(availRes.data);
      const cm = {};
      for (const c of (closedRes.data || [])) cm[c.date] = c.reason || t('calendar.closed');
      setClosedMap(cm);
    } catch (err) {
      console.error('Availability fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode, spaceFilter, t]);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const navigatePeriod = (direction) => {
    // Nobody can move to a period before the current one — bookings can only
    // ever be created in the future, so browsing the past on the calendar
    // adds no value and only confuses users. Admins consult past reservations
    // via the dedicated Admin → Bookings tab.
    if (direction < 0 && !canGoBack) return;
    if (viewMode === 'month') setCurrentDate(addMonths(currentDate, direction));
    else setCurrentDate(addWeeks(currentDate, direction));
  };

  // True when going one step back would land BEFORE the current real-world period.
  // We compare period starts (startOfMonth / startOfWeek) so the user can still
  // be inside the current period and see "today". Applies to everyone (including
  // admins) per the product decision on 2026-07: the calendar is a forward-only
  // booking tool; retrospective reporting lives in the Admin panel.
  const canGoBack = (() => {
    const now = new Date();
    if (viewMode === 'month') {
      return startOfMonth(currentDate).getTime() > startOfMonth(now).getTime();
    }
    return startOfWeek(currentDate, { weekStartsOn: 1 }).getTime()
         > startOfWeek(now, { weekStartsOn: 1 }).getTime();
  })();

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="calendar-page">
      <SEO titleKey="seo.calendar.title" descriptionKey="seo.calendar.description" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A3C34] tracking-tight">{t('calendar.title')}</h1>
            <p className="mt-1 text-sm text-[#1A3C34] font-medium font-body">{t('calendar.subtitle')}</p>
          </div>
          <button data-testid="calendar-book-btn" onClick={() => navigate('/plan')} className="bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg h-11 px-6 text-sm font-bold flex items-center gap-2 transition-all shadow-sm">
            <MapPin className="w-4 h-4" /> {t('calendar.bookSpace')}
          </button>
        </div>

        {/* Controls */}
        <div className="bg-white border border-[#E2E2DF] rounded-xl p-4 mb-6 shadow-sm" data-testid="calendar-controls">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                data-testid="calendar-prev"
                variant="outline"
                size="sm"
                onClick={() => navigatePeriod(-1)}
                disabled={!canGoBack}
                title={!canGoBack ? t('calendar.noPast', 'Vous ne pouvez pas revenir avant le mois en cours') : undefined}
                className="h-9 w-9 p-0 border-[#E2E2DF] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="font-heading text-lg font-bold text-[#1A3C34] min-w-[200px] text-center capitalize">
                {viewMode === 'month'
                  ? format(currentDate, 'MMMM yyyy', { locale: dateLocale })
                  : t('calendar.weekOf', { start: format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM', { locale: dateLocale }), end: format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM yyyy', { locale: dateLocale }) })
                }
              </h2>
              <Button data-testid="calendar-next" variant="outline" size="sm" onClick={() => navigatePeriod(1)} className="h-9 w-9 p-0 border-[#E2E2DF]">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button data-testid="calendar-today" variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="h-9 text-xs font-bold border-[#E2E2DF] text-[#1A3C34] ml-2">
                {t('calendar.today')}
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Select value={spaceFilter} onValueChange={setSpaceFilter}>
                <SelectTrigger data-testid="calendar-space-filter" className="w-44 h-9 border-[#E2E2DF] rounded-md text-sm">
                  <SelectValue placeholder={t('calendar.allSpaces')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('calendar.allSpaces')}</SelectItem>
                  {spaces.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name || t(`spaces.names.${s.id}`, { defaultValue: s.id })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex border border-[#E2E2DF] rounded-md overflow-hidden">
                <button data-testid="view-month-btn" onClick={() => setViewMode('month')}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold transition-colors ${viewMode === 'month' ? 'bg-[#1A3C34] text-white' : 'bg-white text-[#1A3C34] hover:bg-[#F0EFEA]'}`}>
                  <LayoutGrid className="w-3 h-3" /> {t('calendar.month')}
                </button>
                <button data-testid="view-week-btn" onClick={() => setViewMode('week')}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold transition-colors ${viewMode === 'week' ? 'bg-[#1A3C34] text-white' : 'bg-white text-[#1A3C34] hover:bg-[#F0EFEA]'}`}>
                  <CalendarDays className="w-3 h-3" /> {t('calendar.week')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="calendar-legend">
          {spaces.filter(s => spaceFilter === 'all' || s.id === spaceFilter).map(s => (
            <div key={s.id} className="flex items-center gap-1.5 text-xs font-semibold text-[#1A3C34]">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.name}
            </div>
          ))}
          {/* Separator */}
          <div className="w-px h-4 bg-[#E2E2DF] mx-1 hidden sm:block" />
          {/* Today marker */}
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#1A3C34]" data-testid="legend-today">
            <div className="w-4 h-4 rounded-sm border-2 border-[#C77D63] bg-[#FFF7F2]" />
            {t('calendar.legendToday', { defaultValue: "Aujourd'hui" })}
          </div>
          {/* Past marker */}
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#1A3C34]" data-testid="legend-past">
            <div
              className="w-4 h-4 rounded-sm border border-[#E2E2DF]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(45deg, transparent 0 2px, rgba(26,60,52,0.25) 2px 3px)',
                backgroundColor: '#F4F3EE',
              }}
            />
            {t('calendar.legendPast', { defaultValue: 'Passé' })}
          </div>
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : viewMode === 'month' ? (
          <MonthView currentDate={currentDate} calendarData={calendarData} closedMap={closedMap} spaceFilter={spaceFilter} navigate={navigate} spaces={spaces} onDayClick={setSelectedDay} />
        ) : (
          <WeekView currentDate={currentDate} calendarData={calendarData} closedMap={closedMap} spaceFilter={spaceFilter} navigate={navigate} spaces={spaces} />
        )}

        {/* Day detail modal */}
        {selectedDay && (
          <DayDetailModal
            dateStr={selectedDay}
            calendarData={calendarData}
            spaces={spaces}
            spaceFilter={spaceFilter}
            onClose={() => setSelectedDay(null)}
            onBook={(dateStr) => { setSelectedDay(null); navigate(`/plan?date=${dateStr}`); }}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// HELPER: merge consecutive slots per space
// ============================================
function mergeSlots(rawSlots) {
  // Group by space + booking_id to merge consecutive hours of same booking
  const byKey = {};
  for (const slot of rawSlots) {
    const key = `${slot.space_id || ''}_${slot.booking_id || slot.start_time}`;
    if (!byKey[key]) {
      byKey[key] = { ...slot };
    } else {
      // Extend the time range
      if (slot.start_time < byKey[key].start_time) byKey[key].start_time = slot.start_time;
      if (slot.end_time > byKey[key].end_time) byKey[key].end_time = slot.end_time;
    }
  }
  return Object.values(byKey).sort((a, b) => a.start_time.localeCompare(b.start_time));
}

// ============================================
// DAY DETAIL MODAL
// ============================================
function DayDetailModal({ dateStr, calendarData, spaces, spaceFilter, onClose, onBook }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('nl') ? nl : i18n.language?.startsWith('en') ? enUS : fr;
  const bookings = calendarData[dateStr] || {};
  const spaceBookings = spaceFilter === 'all'
    ? Object.entries(bookings)
    : Object.entries(bookings).filter(([sid]) => sid === spaceFilter);

  const allSlots = [];
  for (const [sid, slots] of spaceBookings) {
    const space = spaces.find(s => s.id === sid);
    for (const slot of slots) {
      allSlots.push({ ...slot, space_id: sid, space });
    }
  }
  const merged = mergeSlots(allSlots);

  const displayDate = (() => {
    try { return format(new Date(dateStr + 'T00:00:00'), 'EEEE d MMMM yyyy', { locale: dateLocale }); }
    catch { return dateStr; }
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="day-detail-modal">
        {/* Header */}
        <div className="bg-[#1A3C34] px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-heading text-lg font-semibold capitalize">{displayDate}</h3>
            <p className="text-white/60 text-xs mt-0.5">{t('calendar.bookings', { count: merged.length })}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Timeline */}
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          {merged.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 text-[#E2E2DF] mx-auto mb-2" />
              <p className="text-sm text-[#737373]">{t('calendar.noBookingsThatDay')}</p>
              <p className="text-xs text-[#737373] mt-1">{t('calendar.allAvailable')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {merged.map((slot, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-md border border-[#E2E2DF]"
                  data-testid={`modal-slot-${i}`}
                >
                  <div className="w-2 h-full min-h-[40px] rounded-full flex-shrink-0" style={{ backgroundColor: slot.space?.color || '#737373' }} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#1A3C34]">{(slot.space?.name) || t(`spaces.names.${slot.space_id}`, { defaultValue: slot.space_id })}</div>
                    <div className="text-xs text-[#737373] flex items-center gap-2 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {slot.start_time} - {slot.end_time}
                      {slot.booking_type === 'weekly' && (
                        <span className="bg-[#C77D63]/10 text-[#C77D63] text-[9px] px-1.5 py-0.5 rounded font-medium">{t('calendar.weekly')}</span>
                      )}
                      {slot.booking_type === 'daily' && (
                        <span className="bg-[#4A90D9]/10 text-[#4A90D9] text-[9px] px-1.5 py-0.5 rounded font-medium">{t('calendar.daily')}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#E2E2DF] px-5 py-3">
          <button onClick={() => onBook(dateStr)} className="w-full h-11 bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-sm">
            <ArrowRight className="w-4 h-4" /> {t('calendar.bookForThisDate')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MONTH VIEW
// ============================================
function MonthView({ currentDate, calendarData, closedMap, spaceFilter, navigate, spaces, onDayClick }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('nl') ? nl : i18n.language?.startsWith('en') ? enUS : fr;
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });
  // Compute localized weekday short names dynamically (Mon..Sun)
  const dayNames = Array.from({ length: 7 }, (_, i) => {
    const ref = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), i);
    const name = format(ref, 'EEE', { locale: dateLocale });
    return name.charAt(0).toUpperCase() + name.slice(1);
  });

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden shadow-sm" data-testid="month-view">
      <div className="grid grid-cols-7 border-b border-[#E2E2DF]">
        {dayNames.map(d => (
          <div key={d} className="py-2 text-center text-xs font-bold text-[#1A3C34] border-r last:border-r-0 border-[#E2E2DF] bg-[#F9F9F7]">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const bookings = calendarData[dateStr] || {};
          const isClosed = !!closedMap?.[dateStr];
          const closedReason = closedMap?.[dateStr];
          const inMonth = isSameMonth(day, currentDate);
          const today = isToday(day);
          const past = isBefore(day, new Date()) && !today;

          const spaceBookings = spaceFilter === 'all'
            ? Object.entries(bookings)
            : Object.entries(bookings).filter(([sid]) => sid === spaceFilter);

          // Flatten all slots for this day
          const allSlots = [];
          for (const [sid, slots] of spaceBookings) {
            const space = spaces.find(s => s.id === sid);
            for (const slot of slots) {
              allSlots.push({ ...slot, space_id: sid, space });
            }
          }
          const merged = mergeSlots(allSlots);

          return (
            <div
              key={i}
              data-testid={`month-day-${dateStr}`}
              style={past ? {
                // Diagonal hatched pattern (darker) for past dates so the
                // user instantly distinguishes past from future. Booking
                // events are hidden on past days: they must feel truly
                // "gone" — empty and closed.
                backgroundImage:
                  'repeating-linear-gradient(45deg, transparent 0 6px, rgba(26,60,52,0.10) 6px 8px)',
                backgroundColor: '#F4F3EE',
              } : undefined}
              className={`relative min-h-[100px] p-1.5 border-r border-b border-[#E2E2DF] last:border-r-0 transition-colors ${
                !inMonth ? 'bg-[#F9F9F7] opacity-40' : ''
              } ${today ? 'bg-[#FFF7F2] ring-2 ring-inset ring-[#C77D63] z-[1]' : ''} ${past ? 'text-[#737373]' : ''} ${
                isClosed ? 'bg-red-50' : ''
              } ${
                !past && inMonth && !isClosed ? 'cursor-pointer hover:bg-[#FAFAF8]' : ''
              } ${isClosed ? 'cursor-not-allowed' : ''}`}
              onClick={() => {
                if (!past && inMonth && !isClosed) onDayClick(dateStr);
              }}
            >
              {/* Closed banner */}
              {isClosed && (
                <div
                  className="absolute inset-x-0 top-6 bg-red-600 text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-center py-0.5 px-1 z-10 truncate"
                  data-testid={`closed-banner-${dateStr}`}
                  title={closedReason}
                >
                  {t('closed.label')} - {closedReason}
                </div>
              )}
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-black ${
                  today ? 'bg-[#C77D63] text-white w-6 h-6 rounded-full flex items-center justify-center shadow-sm' :
                  isClosed ? 'text-red-600 line-through' :
                  past ? 'text-[#737373]/80' :
                  inMonth ? 'text-[#1A3C34]' : 'text-[#737373]'
                }`}>
                  {format(day, 'd')}
                </span>
                {!isClosed && !past && merged.length > 0 && (
                  <Badge className="bg-[#1A3C34]/10 text-[#1A3C34] text-[10px] px-1 py-0 h-4 font-bold">
                    {merged.length}
                  </Badge>
                )}
              </div>
              {!isClosed && !past && (
                <div className="space-y-0.5">
                  {merged.slice(0, 3).map((slot, si) => (
                    <div
                      key={si}
                      className="text-[10px] px-1 py-0.5 rounded truncate font-bold leading-tight"
                      style={{ backgroundColor: slot.space?.color || '#737373', color: readableText(slot.space?.color) }}
                      title={`${slot.space?.name}: ${slot.start_time}-${slot.end_time}`}
                    >
                      {slot.space?.shortName} {slot.start_time}-{slot.end_time}
                    </div>
                  ))}
                  {merged.length > 3 && (
                    <div className="text-[10px] text-[#C77D63] font-bold px-1 cursor-pointer">
                      {t('common.more', { count: merged.length - 3 })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// WEEK VIEW
// ============================================
function WeekView({ currentDate, calendarData, closedMap, spaceFilter, navigate, spaces }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('nl') ? nl : i18n.language?.startsWith('en') ? enUS : fr;
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden overflow-x-auto shadow-sm" data-testid="week-view">
      <div className="min-w-[800px]">
        {/* Header row */}
        <div className="grid" style={{ gridTemplateColumns: '70px repeat(7, 1fr)' }}>
          <div className="p-2 border-r border-b border-[#E2E2DF] bg-[#F9F9F7]">
            <Clock className="w-4 h-4 text-[#737373] mx-auto" />
          </div>
          {days.map(day => {
            const today = isToday(day);
            const dateStr = format(day, 'yyyy-MM-dd');
            const closedReason = closedMap?.[dateStr];
            const past = isBefore(day, new Date()) && !today;
            return (
              <div
                key={day.toISOString()}
                style={past && !today && !closedReason ? {
                  backgroundImage:
                    'repeating-linear-gradient(45deg, transparent 0 6px, rgba(26,60,52,0.10) 6px 8px)',
                  backgroundColor: '#F4F3EE',
                } : undefined}
                className={`p-2 text-center border-r last:border-r-0 border-b border-[#E2E2DF] ${
                  today ? 'bg-[#FFF7F2] ring-2 ring-inset ring-[#C77D63] relative z-[1]'
                  : closedReason ? 'bg-red-50'
                  : past ? '' : 'bg-[#F9F9F7]'
                }`}
              >
                <div className={`text-[11px] uppercase font-bold ${past ? 'text-[#737373]/80' : 'text-[#1A3C34]'}`}>{format(day, 'EEE', { locale: dateLocale })}</div>
                <div className={`text-base font-black mt-0.5 ${today ? 'text-white bg-[#C77D63] w-7 h-7 rounded-full flex items-center justify-center mx-auto shadow-sm' : closedReason ? 'text-red-600 line-through' : past ? 'text-[#737373]/80' : 'text-[#1A3C34]'}`}>
                  {format(day, 'd')}
                </div>
                {closedReason && (
                  <div className="mt-1 bg-red-600 text-white text-[8px] font-bold uppercase tracking-wider rounded px-1 py-0.5 truncate" title={closedReason}>
                    {t('closed.label')}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Time slots */}
        {HOURS.map(hour => (
          <div key={hour} className="grid" style={{ gridTemplateColumns: '70px repeat(7, 1fr)' }}>
            <div className="p-1.5 text-right pr-3 border-r border-b border-[#E2E2DF] bg-[#F9F9F7]">
              <span className="text-[11px] font-mono font-bold text-[#1A3C34]">{String(hour).padStart(2, '0')}:00</span>
            </div>
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayBookings = calendarData[dateStr] || {};
              const hourStr = `${String(hour).padStart(2, '0')}:00`;
              const nextHourStr = `${String(hour + 1).padStart(2, '0')}:00`;
              const past = isBefore(day, new Date()) && !isToday(day);
              const isClosed = !!closedMap?.[dateStr];

              const slotBookings = [];
              for (const [sid, slots] of Object.entries(dayBookings)) {
                if (spaceFilter !== 'all' && sid !== spaceFilter) continue;
                for (const slot of slots) {
                  if (slot.start_time < nextHourStr && slot.end_time > hourStr) {
                    const space = spaces.find(s => s.id === sid);
                    slotBookings.push({ ...slot, space_id: sid, space });
                  }
                }
              }
              // Merge to avoid duplicate display for same booking across hours
              const merged = mergeSlots(slotBookings);
              // Only show if this is the start hour of the merged slot
              const toShow = merged.filter(s => {
                const startH = parseInt(s.start_time.split(':')[0]);
                return startH === hour;
              });

              return (
                <div
                  key={`${dateStr}-${hour}`}
                  data-testid={`week-slot-${dateStr}-${hour}`}
                  role="button"
                  tabIndex={past || isClosed ? -1 : 0}
                  style={past && !isClosed ? {
                    backgroundImage:
                      'repeating-linear-gradient(45deg, transparent 0 6px, rgba(26,60,52,0.10) 6px 8px)',
                    backgroundColor: '#F4F3EE',
                  } : undefined}
                  className={`min-h-[36px] p-0.5 border-r last:border-r-0 border-b border-[#E2E2DF] transition-colors relative ${
                    isClosed ? 'bg-red-50 cursor-not-allowed' :
                    past ? 'cursor-not-allowed' : 'hover:bg-[#F0EFEA] cursor-pointer'
                  }`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!past && !isClosed) navigate(`/plan?date=${dateStr}&time=${hourStr}`); }}
                >
                  {!past && toShow.map((sb, i) => {
                    const startH = parseInt(sb.start_time.split(':')[0]);
                    const endH = parseInt(sb.end_time.split(':')[0]);
                    const spanHours = endH - startH;
                    return (
                      <div
                        key={i}
                        className="text-[9px] px-1 py-0.5 rounded font-bold truncate mb-0.5 pointer-events-none"
                        style={{
                          backgroundColor: sb.space?.color || '#737373',
                          color: readableText(sb.space?.color),
                          height: spanHours > 1 ? `${spanHours * 36 - 4}px` : undefined,
                          position: spanHours > 1 ? 'absolute' : undefined,
                          left: spanHours > 1 ? '2px' : undefined,
                          right: spanHours > 1 ? '2px' : undefined,
                          zIndex: spanHours > 1 ? 2 : undefined,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title={`${sb.space?.name}: ${sb.start_time}-${sb.end_time}`}
                      >
                        {sb.space?.shortName} {sb.start_time}-{sb.end_time}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
